import Fuse from "fuse.js";
import { uniq } from "lodash-es";

import type { ConstraintListItemDto } from "@/api/types";

export function addressTagsToDictionary(
  tags: ConstraintListItemDto[] = [],
): Partial<Record<ConstraintListItemDto["type"], ConstraintListItemDto["key"]>> {
  return Object.fromEntries(tags.map((x) => [x.type, x.key]));
}

export function formatAddress(x: { streetName: string; houseNumber: string; houseNumberSuffix?: string }): string {
  return `${x.streetName} ${x.houseNumber} ${x.houseNumberSuffix ?? ""}`.trim();
}

const HOUSE_NUMBER_SPLIT_REGEX = /^(.+?)\s+(\d+)[\s-]*([a-zA-Z0-9]*)\s*$/;

export function splitAddress(
  fullAddress: string,
): { streetName: string; houseNumber: string; houseNumberSuffix: string } | undefined {
  const match = fullAddress.trim().match(HOUSE_NUMBER_SPLIT_REGEX);
  if (!match) {
    return undefined;
  }

  const [, streetName, houseNumber, houseNumberSuffix] = match;

  return { streetName: streetName.trim(), houseNumber, houseNumberSuffix };
}

// A house number is leading digits, optionally followed by a suffix glued or spaced onto it
// (e.g. "12", "12a", "12-b", "7 bis"). Used both to validate the bulk-upload "House number" column
// and to split a glued value into its number + suffix on submit.
export const HOUSE_NUMBER_WITH_SUFFIX_REGEX = /^(\d+)[\s-]*([a-zA-Z0-9]*)$/;

export function splitHouseNumber(
  value: string,
): { houseNumber: number; houseNumberSuffix: string | undefined } | undefined {
  const match = value.trim().match(HOUSE_NUMBER_WITH_SUFFIX_REGEX);
  if (!match) {
    return undefined;
  }

  return { houseNumber: Number(match[1]), houseNumberSuffix: match[2] || undefined };
}

type AddressField = "street" | "houseNumber" | "houseNumberSuffix" | "combined";

const ALIAS_GROUPS: { field: AddressField; aliases: readonly string[] }[] = [
  { field: "street", aliases: ["street", "streetname", "street name", "straat", "straatnaam"] },
  { field: "houseNumber", aliases: ["house number", "housenumber", "huisnummer", "huisnr", "huisnr."] },
  { field: "houseNumberSuffix", aliases: ["house number suffix", "huisnummertoevoeging"] },
  {
    field: "combined",
    aliases: ["address", "adres", "full address", "street address", "volledig adres"],
  },
];

// Same threshold used for column-header matching in DataTable/parser.ts, so a header like
// "Huis nr" or "Straatnaam " (typos, extra whitespace) still resolves to the intended alias.
const COLUMN_NAME_MATCH_THRESHOLD = 0.2;

const SEARCHABLE_ALIASES = ALIAS_GROUPS.flatMap(({ field, aliases }) => aliases.map((alias) => ({ field, alias })));

/**
 * Matches each raw column name in a sheet to the address field it most closely resembles. Every
 * column is matched against all aliases at once (rather than checking one field's aliases at a
 * time) so a column like "housenumber" resolves to "houseNumber" and not also, more loosely, to
 * "houseNumberSuffix" - each column can only fill a single field.
 *
 * Matching runs over the union of every row's keys rather than per row, because the sheet parser
 * drops blank cells: a row that left "huisnr" empty has no "huisnr" key at all. Deciding per row
 * would make such a row look like it has no house number column and send it down a different
 * branch than its neighbours, leaving the sheet with two rival house number columns.
 */
function matchAddressColumns(rows: object[]): Partial<Record<AddressField, string>> {
  const fuse = new Fuse(SEARCHABLE_ALIASES, { keys: ["alias"], threshold: COLUMN_NAME_MATCH_THRESHOLD });
  const matches: Partial<Record<AddressField, string>> = {};

  for (const key of uniq(rows.flatMap((row) => Object.keys(row)))) {
    const field = fuse.search(key.trim().toLowerCase())[0]?.item.field;
    if (field && !matches[field]) {
      matches[field] = key;
    }
  }

  return matches;
}

function getStringValue(row: object, key: string | undefined): string | undefined {
  if (!key) {
    return undefined;
  }

  const value = (row as Record<string, unknown>)[key];
  if (typeof value === "number") {
    return value.toString();
  }

  return typeof value === "string" ? value : undefined;
}

/**
 * Bulk upload rows may contain a single combined address column (e.g. "Kerkstraat 12a") instead of
 * separate street/house number/suffix columns. Splits it into those separate columns so it can be
 * mapped by the regular column parser, unless the row already has separate columns.
 *
 * The combined value isn't always under a dedicated "Address"-style column - sheets often label it
 * "Street"/"Straat"/"Adres" even though it contains the house number too. So when there's no house
 * number column, the street-aliased column is also tried as a source of the combined value.
 *
 * A sheet may also have a combined column next to a separate house number column (e.g. "Adres" =
 * "Anke Servaespad 1" alongside "huisnr" = "1"). Then the combined column is really the street, so
 * only its street part is kept and the number is left to the dedicated column. A "Street" key is
 * written for every such row - including rows whose value has no number to strip - so the resulting
 * column set is identical across rows; otherwise the parser, which maps columns once for the whole
 * sheet, would map "Street" to the injected key and drop the rows that never got one.
 *
 * Such a sheet often leaves the dedicated house number column blank on some rows, with only the
 * combined value carrying the number ("Adres" = "Molenstraat 34", "huisnr" = ""). Stripping the
 * number off those rows would lose it entirely, so the stripped number stands in for the blank
 * cell instead.
 */
export function splitCombinedAddressColumn(rows: object[]): object[] {
  const matches = matchAddressColumns(rows);
  // With both columns present the street column is the one that may still carry a house number, so
  // it, rather than any combined column, is what gets split.
  const source = matches.street && matches.houseNumber ? matches.street : (matches.combined ?? matches.street);

  return rows.map((row) => {
    const combined = getStringValue(row, source);
    if (!combined) {
      return row;
    }

    const split = splitAddress(combined);

    if (matches.houseNumber) {
      const street = { ...row, Street: split ? split.streetName : combined };

      // The dedicated column wins where it has a value. Where it's blank, the number stripped off
      // the combined value fills in, glued back onto its suffix so splitHouseNumber() can take the
      // two apart again on submit.
      if (!split || getStringValue(row, matches.houseNumber)?.trim()) {
        return street;
      }

      return { ...street, [matches.houseNumber]: `${split.houseNumber}${split.houseNumberSuffix}` };
    }

    // Without a house number column to corroborate it, a value that doesn't split into street +
    // number is left alone - the source column may be an address-ish false positive under fuzzy
    // matching (a "Name" column resembles the "street name" alias) rather than a real street.
    if (!split) {
      return row;
    }

    return {
      ...row,
      Street: split.streetName,
      "House number": split.houseNumber,
      "House number suffix": split.houseNumberSuffix,
    };
  });
}

/**
 * Bulk upload rows may contain separate street/house number/suffix columns instead of a single
 * combined address column. Joins them into a combined "Address" column so it can be matched
 * against existing addresses.
 *
 * A row may also already have a combined column (e.g. "adres": "Kerkstraat 12a"), but its text
 * needs to match the canonical space-joined format produced by formatAddress() exactly, since
 * matching is a plain string comparison. So it's re-split and reformatted to normalize it, rather
 * than trusting the raw text as-is.
 *
 * The house number itself can also carry a glued-on suffix (e.g. "House number": "12a" with no
 * separate suffix column), so the same re-split/reformat normalization is applied there too.
 */
export function joinSplitAddressColumns(rows: object[]): object[] {
  const matches = matchAddressColumns(rows);

  return rows.map((row) => {
    const combined = getStringValue(row, matches.combined);
    if (combined) {
      const split = splitAddress(combined);

      return split ? { ...row, Address: formatAddress(split) } : row;
    }

    const streetName = getStringValue(row, matches.street);
    const houseNumber = getStringValue(row, matches.houseNumber);
    if (!streetName || !houseNumber) {
      return row;
    }

    const houseNumberSuffix = getStringValue(row, matches.houseNumberSuffix);
    const fallback = { streetName, houseNumber, houseNumberSuffix };
    const split = splitAddress(formatAddress(fallback));

    return {
      ...row,
      Address: formatAddress(split ?? fallback),
    };
  });
}
